import {
  BLASTNUiParamsType,
  CandidateBLASTNUiParamsType,
  getCandidateBLASTNUiParams,
} from 'search/blast/parameters/blastnConstants';
import {
  BLASTPUiParamsType,
  CandidateBLASTPUiParamsType,
  getCandidateBLASTPUiParams,
} from 'search/blast/parameters/blastpConstants';
import {
  BLASTXUiParamsType,
  CandidateBLASTXUiParamsType,
  getCandidateBLASTXUiParams,
} from 'search/blast/parameters/blastxConstants';
import {
  CandidateTBLASTNUiParamsType,
  getCandidateTBLASTNUiParams,
  TBLASTNUiParamsType,
} from 'search/blast/parameters/tblastnConstants';
import {
  CandidateTBLASTXUiParamsType,
  getCandidateTBLASTXUiParams,
  TBLASTXUiParamsType,
} from 'search/blast/parameters/tblastxConstants';
import {
  AllBLASTParams,
  BLASTFilter,
  BLASTGapCost,
  BLASTMatchScore,
  BLASTMatrix,
  BLASTNTask,
  BLASTOperator,
  BLASTParamsTypeMap,
  BLASTParamsUnion,
  BLASTPTask,
  BLASTTaskTypeMap,
  BLASTXTask,
  hasMatchScoreParam,
  hasMatrixParam,
  hasTaskParam,
  Operator,
  TBLASTNTask,
} from 'search/SearchConstants';

export interface CandidateBLASTUiParamsTypeMap {
  [Operator.BLASTN]: CandidateBLASTNUiParamsType;
  [Operator.BLASTP]: CandidateBLASTPUiParamsType;
  [Operator.BLASTX]: CandidateBLASTXUiParamsType;
  [Operator.TBLASTN]: CandidateTBLASTNUiParamsType;
  [Operator.TBLASTX]: CandidateTBLASTXUiParamsType;
}

export interface BLASTUiParamsTypeMap {
  [Operator.BLASTN]: BLASTNUiParamsType;
  [Operator.BLASTP]: BLASTPUiParamsType;
  [Operator.BLASTX]: BLASTXUiParamsType;
  [Operator.TBLASTN]: TBLASTNUiParamsType;
  [Operator.TBLASTX]: TBLASTXUiParamsType;
}

export type CandidateBLASTUiParams = CandidateBLASTUiParamsTypeMap[keyof CandidateBLASTUiParamsTypeMap];

export const getCandidateBLASTUiParams = <T extends BLASTOperator>(config: {
  blastOperator: T;
  taskType?: BLASTTaskTypeMap[T];
  matrix?: BLASTMatrix;
  matchScore?: BLASTMatchScore;
}): CandidateBLASTUiParamsTypeMap[T] => {
  const {blastOperator, taskType, matrix, matchScore} = config;
  switch (blastOperator) {
    case Operator.BLASTN:
      return getCandidateBLASTNUiParams(
        taskType as BLASTNTask | undefined,
        matchScore
      ) as CandidateBLASTUiParamsTypeMap[T];
    case Operator.BLASTP:
      return getCandidateBLASTPUiParams(
        taskType as BLASTPTask | undefined,
        matrix
      ) as CandidateBLASTUiParamsTypeMap[T];
    case Operator.BLASTX:
      return getCandidateBLASTXUiParams(
        taskType as BLASTXTask | undefined,
        matrix
      ) as CandidateBLASTUiParamsTypeMap[T];
    case Operator.TBLASTN:
      return getCandidateTBLASTNUiParams(
        taskType as TBLASTNTask | undefined,
        matrix
      ) as CandidateBLASTUiParamsTypeMap[T];
    case Operator.TBLASTX:
      return getCandidateTBLASTXUiParams() as CandidateBLASTUiParamsTypeMap[T];
    default:
      throw new Error(`Unsupported BLAST operator: ${blastOperator}`);
  }
};

export const getCandidateBLASTUiParamsFromFilter = <T extends BLASTOperator>(
  filter: BLASTFilter
): CandidateBLASTUiParamsTypeMap[T] => {
  const taskType: BLASTNTask | BLASTPTask | BLASTXTask | TBLASTNTask | undefined =
    filter.params && hasTaskParam(filter.params) ? filter.params.task : undefined;
  const matrix: BLASTMatrix | undefined =
    filter.params && hasMatrixParam(filter.params) ? filter.params.matrix : undefined;
  const matchScore: BLASTMatchScore | undefined =
    filter.params && hasMatchScoreParam(filter.params)
      ? {reward: filter.params.reward!, penalty: filter.params.penalty!}
      : undefined;
  return getCandidateBLASTUiParams<T>({
    blastOperator: filter.operator as T,
    taskType: taskType as BLASTTaskTypeMap[T],
    matrix,
    matchScore,
  });
};

/**
 * Return recommended short task params for < 30 residues, or null if not applicable.
 * The client is responsible for checking the residue length.
 */
export const getCandidateBLASTUiParamsForShortResidues = <T extends BLASTOperator>(
  blastOperator: T,
  matchScore?: BLASTMatchScore
): CandidateBLASTUiParamsTypeMap[T] | null => {
  switch (blastOperator) {
    case Operator.BLASTN:
      return getCandidateBLASTNUiParams(
        BLASTNTask.BLASTNShort,
        matchScore
      ) as CandidateBLASTUiParamsTypeMap[T];
    case Operator.BLASTP:
      return getCandidateBLASTPUiParams(BLASTPTask.BLASTPShort) as CandidateBLASTUiParamsTypeMap[T];
    default:
      return null;
  }
};

// candidate ui params => ui params
export const getDefaultBLASTUiParams = <T extends BLASTOperator>(
  candidateUiParams: CandidateBLASTUiParamsTypeMap[T]
): BLASTUiParamsTypeMap[T] => {
  const editModeParams = {} as BLASTUiParamsTypeMap[T];
  for (const [key, params] of Object.entries(candidateUiParams)) {
    editModeParams[key as keyof BLASTUiParamsTypeMap[T]] = params.defaultValue;
  }
  return editModeParams;
};

// ui params -> blast params
export const getBLASTParams = <T extends BLASTOperator>(
  uiParams: BLASTUiParamsTypeMap[T]
): BLASTParamsTypeMap[T] => {
  const defaultParams = {} as AllBLASTParams<BLASTTaskTypeMap[T]>;
  for (const [key, value] of Object.entries(uiParams)) {
    if (key === 'gap_cost') {
      const {existence, extension} = value as BLASTGapCost;
      defaultParams.gapopen = existence;
      defaultParams.gapextend = extension;
    } else if (key === 'match_score') {
      const {reward, penalty} = value as BLASTMatchScore;
      defaultParams.reward = reward;
      defaultParams.penalty = penalty;
    } else {
      defaultParams[key as keyof AllBLASTParams<BLASTTaskTypeMap[T]>] = value;
    }
  }
  return defaultParams as BLASTParamsTypeMap[T];
};

export const getBLASTParamsFromFilter = (filter: BLASTFilter): BLASTParamsUnion =>
  getBLASTParams(getDefaultBLASTUiParams(getCandidateBLASTUiParamsFromFilter(filter)));

/**
 * If a new task type is specified, return the default params for that task.
 * Otherwise, use the default task type.
 */
export const getDefaultBLASTParamsForTask = (
  filter: BLASTFilter,
  newTaskType?: BLASTNTask | BLASTPTask | BLASTXTask | TBLASTNTask
): BLASTParamsUnion =>
  getBLASTParams(
    getDefaultBLASTUiParams(
      getCandidateBLASTUiParams({
        blastOperator: filter.operator as BLASTOperator,
        taskType: newTaskType,
      })
    )
  );
